/**
 * Court Connect partner referral-interest form — /partner-with-us (event QR
 * landing). Short by design (approved copy 2026-09-11): website, current
 * payment setup and notes are deferred to the follow-up conversation.
 */
import { Mail } from "lucide-react";
import analytics from "@/utils/analytics";
import { company, email, firstName, lastName, phone } from "./common";

/** "I work as a" → Zoho Partner_Type; "Other" stays in the Note. */
const ROLE_OPTIONS = [
  { value: "Developer", label: "Developer", crm: "Developer" },
  { value: "Web agency", label: "Web agency", crm: "Agency" },
  { value: "Platform provider", label: "Platform provider", crm: "Developer" },
  { value: "Consultant", label: "Consultant", crm: "Consultant" },
  { value: "Other", label: "Other", crm: null },
];

const courtConnectPartner = {
  id: "court_connect_partner",
  label: "Court Connect partner enquiry",
  crm: { layout: "Partner", leadSource: "Website Partner Enquiry", tags: ["Court Connect"] },
  fields: [
    [
      { ...firstName, label: "First Name", requiredMessage: "Please enter your first name." },
      { ...lastName, label: "Last Name", requiredMessage: "Please enter your last name." },
    ],
    { ...email, label: "Work Email", requiredMessage: "Please enter your work email." },
    { ...company, label: "Company" },
    phone,
    {
      name: "role", type: "select", label: "I work as a", placeholder: "Select…",
      options: ROLE_OPTIONS, crm: "Partner_Type",
    },
  ],
  submitLabel: "Register my interest",
  consent: { variant: "condensed" },
  footer: (
    <p className="text-center text-sm text-brand-grey pt-2">
      {"Prefer email? Contact "}
      <a
        href="mailto:hello@candopayments.com"
        className="inline-flex items-center gap-1.5 font-semibold text-brand-aqua-dark hover:text-brand-aqua transition-colors"
        data-testid="cc-form-email-link"
      >
        <Mail size={14} /> hello@candopayments.com
      </a>
    </p>
  ),
  // Rendered only client-side after a failed request, so markup is safe here.
  errorMessage: (
    <>
      {"We couldn’t send your details. Please try again, or contact us at "}
      <a href="mailto:hello@candopayments.com" className="font-semibold underline underline-offset-2">
        hello@candopayments.com
      </a>
      .
    </>
  ),
  success: {
    title: "Thanks — we’ve received your interest.",
    body: "A member of the CanDo team will contact you about the clients you support and how the partner programme works.",
  },
  summary: (values) => ["Court Connect partner enquiry", values.role].filter(Boolean).join(" · "),
  // Landing-page funnel events kept from the previous form.
  events: {
    start: () => analytics.landingEvent("cc_partner_form_start"),
    submit: () => analytics.landingEvent("cc_partner_form_submit"),
    success: () => analytics.partnerLeadSubmitted("court_connect_partner_enquiry"),
    invalid: () => analytics.landingEvent("cc_partner_form_error", { reason: "validation" }),
    error: () => analytics.landingEvent("cc_partner_form_error", { reason: "request" }),
  },
};

export default courtConnectPartner;
